import { Form, Row, Select, Space, Switch, Tag } from "antd";
import { PaginationProps } from "antd/lib/pagination";
import { ColumnProps } from "antd/lib/table";
import { SorterResult, SortOrder } from "antd/lib/table/interface";
import DatePicker from "Components/General/DatePicker";
import { SearchBar } from "Components/General/SearchBar";
import { Table } from "Components/General/Table";
import { I18N_NS } from "Config/i18n";
import { dayjs } from "Config/dayjs";
import { isEqual, uniqWith } from "lodash";
import { observer } from "mobx-react";
import { ProjectUsersDirectoryParams, ProjectUsersStore } from "Pages/Admin/Stores/ProjectUsersStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { numberSort, stringSort } from "utils/defaultSort";
import { getProjectRoleSelectOptions, getProjectRoleTableFilters } from "utils/projectRole";

export interface ProjectMembersEditorProps {
  store: ProjectUsersStore;
  getUserEditUrl: (user: API_ProjectUser) => string;
  /* When set, users beyond the project members can be listed, scoped to that project group */
  projectGroupId?: number;
  tableName: string;
  afterLinkClick?: () => void;
}

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = ["10", "30", "50", "75", "100", "150", "200"];

/* Only columns the users endpoint can sort by */
const SORT_BY_MAP: { [key in "userId" | "username" | "company" | "email"]: API_USERPAGINATION_SORTBY } = {
  userId: "USER_ID",
  username: "FIRST_NAME",
  company: "COMPANY",
  email: "EMAIL",
};
const SORT_ORDER_MAP: { [key in SortOrder]: API_USERPAGINATION_SORTORDER } = {
  ascend: "ASC",
  descend: "DESC",
};

const matchesSearch = (user: API_ProjectUser, query: string) => {
  if (!query) {
    return true;
  }
  return Object.values(user).some((value) => value != null && String(value).includes(query));
};

export const ProjectMembersEditor: React.FC<ProjectMembersEditorProps> = observer(
  ({ store, getUserEditUrl, projectGroupId, tableName, afterLinkClick }) => {
    const { t } = useTranslation(I18N_NS.ADMIN);
    const [showAssignedOnly, setShowAssignedOnly] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [sorter, setSorter] = useState<SorterResult<API_ProjectUser>>({
      field: "userId",
      columnKey: "userId",
      order: "ascend",
    });
    const [page, setPage] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE });

    const canListAllUsers = projectGroupId != null;
    const isDirectoryMode = canListAllUsers && !showAssignedOnly;

    const directoryParams = useMemo((): ProjectUsersDirectoryParams => {
      return {
        limit: page.pageSize,
        offset: page.current > 1 ? page.pageSize * (page.current - 1) : 0,
        sortBy: SORT_BY_MAP[sorter?.field as keyof typeof SORT_BY_MAP] || "USER_ID",
        sortOrder: sorter?.order ? SORT_ORDER_MAP[sorter.order] : "ASC",
        // Axios is configured with "encode: false", so query values have to be encoded here
        query: encodeURIComponent(searchQuery),
        active: false,
        ...(projectGroupId != null ? { projectGroupId: [projectGroupId] } : {}),
      };
    }, [page.current, page.pageSize, sorter?.field, sorter?.order, searchQuery, projectGroupId]);

    useEffect(() => {
      if (!isDirectoryMode) {
        return;
      }
      void store.fetchDirectory(directoryParams);
    }, [isDirectoryMode, directoryParams, store]);

    const assignedRows = useMemo(
      () => store.assignedUsers.filter((user) => matchesSearch(user, searchQuery)),
      [store.assignedUsers, searchQuery]
    );
    const visibleData = isDirectoryMode ? store.directoryRows : assignedRows;

    const handleTableChange = useCallback(
      (
        nextPagination: PaginationProps,
        _filters: Record<string, unknown>,
        nextSorter: SorterResult<API_ProjectUser> | SorterResult<API_ProjectUser>[]
      ) => {
        setPage({
          current: nextPagination.current || 1,
          pageSize: nextPagination.pageSize || DEFAULT_PAGE_SIZE,
        });
        setSorter(Array.isArray(nextSorter) ? nextSorter[0] : nextSorter);
      },
      []
    );

    const handleSearch = useCallback((value: string) => {
      setSearchQuery(value);
      setPage((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleToggleAssignedOnly = useCallback((checked: boolean) => {
      setShowAssignedOnly(checked);
      setPage((prev) => ({ ...prev, current: 1 }));
    }, []);

    const rightsOptions = useMemo(() => getProjectRoleSelectOptions(t), [t]);

    const columns: ColumnProps<API_ProjectUser>[] = [
      {
        dataIndex: "projectMembershipActive",
        title: t("Project membership"),
        width: 150,
        render: (value, record) => {
          if (record.active) {
            return (
              <Switch
                checked={value}
                size="small"
                checkedChildren={t("Active")}
                unCheckedChildren={t("Inactive")}
                onChange={(checked) => {
                  if (record.id != null) {
                    store.setProjectMembership(record.id, checked);
                  }
                }}
                aria-label={t("Project membership")}
              />
            );
          }
          return <Tag color="gray">{t("User Deactivated")}</Tag>;
        },
      },
      {
        dataIndex: "userProjectRole",
        title: t("Rights"),
        // The users endpoint cannot sort or filter by project role
        sorter: isDirectoryMode ? undefined : (a, b) => stringSort(a, b, "userProjectRole"),
        width: 180,
        render: (value, record) => {
          return (
            <Select
              size="small"
              disabled={!record.active || !record.projectMembershipActive}
              value={value}
              onChange={(nextRole: API_PROJECTUSER_USERPROJECTROLE) => {
                if (record.id != null) {
                  store.setUserProjectRole(record.id, nextRole);
                }
              }}
              options={rightsOptions}
              aria-label={t("Rights")}
            />
          );
        },
        ...(isDirectoryMode
          ? {}
          : {
              filters: getProjectRoleTableFilters(t),
              onFilter: (value: string, record: API_ProjectUser) => (record.userProjectRole ?? "").startsWith(value),
            }),
      },
      {
        dataIndex: "id",
        title: t("User Id"),
        sorter: isDirectoryMode ? true : (a, b) => numberSort(+a.id, +b.id),
        defaultSortOrder: "ascend",
      },
      {
        dataIndex: "userId",
        title: t("Login name"),
        render: (text: string, record: API_ProjectUser) => (
          <Link to={getUserEditUrl(record)} target="_blank">
            {text}
          </Link>
        ),
        sorter: isDirectoryMode ? true : (a, b) => stringSort(a, b, "userId"),
        defaultSortOrder: "ascend",
      },
      {
        dataIndex: "username",
        title: t("Name"),
        sorter: isDirectoryMode ? true : (a, b) => stringSort(a, b, "username"),
      },
      {
        dataIndex: "company",
        title: t("Company"),
        sorter: isDirectoryMode ? true : (a, b) => stringSort(a, b, "company"),
        ...(isDirectoryMode
          ? {}
          : {
              filters: uniqWith(
                visibleData
                  .filter((item) => Boolean(item.company))
                  .map((item) => ({ text: item.company, value: item.company })),
                isEqual
              ),
              onFilter: (value: string, record: API_ProjectUser) => (record.company ?? "").startsWith(value),
            }),
      },
      {
        dataIndex: "email",
        title: t("Email"),
        sorter: isDirectoryMode ? true : (a, b) => stringSort(a, b, "email"),
      },
      {
        dataIndex: "validUntil",
        title: t("Valid until"),
        // The users endpoint cannot sort by the project specific validity
        sorter: isDirectoryMode ? undefined : (a, b) => stringSort(a, b, "validUntil"),
        render: (value, record) => {
          if (!record.projectMembershipActive) {
            return null;
          }
          return (
            <DatePicker
              size="small"
              value={value}
              showTime
              placeHolderButton={t("Not set")}
              placeHolderButtonDisabled={!record.active}
              disabled={!record.active}
              onChange={(_nextValue, dateString) => {
                if (record.id == null) {
                  return;
                }
                const dateValue = Array.isArray(dateString) ? dateString[0] : dateString;
                store.setValidUntil(record.id, dayjs(dateValue).valueOf());
              }}
            />
          );
        },
      },
    ];

    const tablePagination: PaginationProps = {
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      current: page.current,
      pageSize: page.pageSize,
      hideOnSinglePage: true,
      // Assigned members are fully loaded, so antd derives the total from the rows
      total: isDirectoryMode ? store.directoryTotal : undefined,
    };

    return (
      <>
        <Row justify="space-between" align="middle" className="mb-2" style={{ flexWrap: "nowrap" }}>
          <Form style={{ flex: 1, minWidth: 0 }} onSubmitCapture={(event) => event.preventDefault()}>
            <SearchBar
              placeholder={t("Search within user id, name, email and contact data")}
              onSearch={handleSearch}
              style={{ marginRight: 10, marginBottom: 0 }}
            />
          </Form>
          {canListAllUsers && (
            <Space className="ml-md" style={{ flexShrink: 0 }}>
              <span>{t("Show only assigned users")}</span>
              <Switch
                checked={showAssignedOnly}
                onChange={handleToggleAssignedOnly}
                aria-label={t("Show only assigned users")}
              />
            </Space>
          )}
        </Row>
        <Table<API_ProjectUser>
          name={tableName}
          dataSource={visibleData}
          rowKey="id"
          columns={columns}
          rowClassName={(record) => (record?.active ? "" : "disabledCell")}
          loading={store.state === "LOADING"}
          pagination={tablePagination}
          onChange={handleTableChange}
        />
      </>
    );
  }
);
